import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { BRAND } from '@guanwei/shared';
import { ThemeToggleButton } from '@/theme/ThemeSwitcher';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/lib/auth.store';

const PRIMARY_NAV = [
  { to: '/chart', label: '排盘', desc: '生成命盘' },
  { to: '/compatibility', label: '关系合盘', desc: '关系合盘' },
  { to: '/fortune', label: '流年', desc: '年度大运' },
  { to: '/chat', label: '追问', desc: '命盘问答' },
  { to: '/divination', label: '起卦', desc: '六爻问事' },
];

const SECONDARY_NAV = [
  { to: '/membership', label: '会员', desc: '权益套餐' },
  { to: '/invite', label: '邀请', desc: '邀请好友' },
];

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border/80 bg-bg/85 shadow-sm shadow-black/5 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="group flex min-w-0 items-center gap-3 rounded-2xl py-2 pr-2"
          onClick={() => setMenuOpen(false)}
        >
          <span className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-2xl border border-border-accent bg-accent-soft shadow-gold">
            <span className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent" />
            <span className="relative font-serif text-2xl text-accent transition-transform duration-base group-hover:scale-110">
              觀
            </span>
          </span>
          <span className="min-w-0 leading-tight">
            <span className="block font-serif text-lg text-fg">{BRAND.nameZh}</span>
            <span className="block truncate text-[10px] uppercase tracking-[0.32em] text-fg-muted">
              {BRAND.nameEn}
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center lg:flex" aria-label="主导航">
          <div className="flex items-center rounded-full border border-border bg-bg-1/80 p-1 shadow-sm">
            {PRIMARY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    'group relative rounded-full px-4 py-2 text-sm transition-all duration-base',
                    isActive
                      ? 'bg-accent text-accent-fg shadow-md'
                      : 'text-fg-1 hover:bg-bg-2 hover:text-fg',
                  )
                }
              >
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="ml-auto hidden items-center gap-2 md:flex">
          {SECONDARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  'rounded-full px-3 py-2 text-sm transition-colors duration-base',
                  item.to === '/membership'
                    ? isActive
                      ? 'bg-accent text-accent-fg shadow-md'
                      : 'border border-border-accent bg-accent-soft text-accent hover:bg-accent hover:text-accent-fg'
                    : isActive
                      ? 'bg-bg-2 text-fg'
                      : 'text-fg-2 hover:bg-bg-2 hover:text-fg',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          <ThemeToggleButton />
          {user ? (
            <div className="flex items-center gap-2 rounded-full border border-border bg-bg-1 py-1 pl-2 pr-1">
              <Link
                to="/profile"
                className="max-w-32 truncate px-2 text-sm text-fg-2 transition-colors hover:text-accent"
                title={user.name ?? user.email}
              >
                {user.name ?? user.email}
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full">
                登出
              </Button>
            </div>
          ) : (
            <Link to="/auth/login">
              <Button variant="outline" size="sm" className="rounded-full">
                登录
              </Button>
            </Link>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2 md:hidden">
          <ThemeToggleButton />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bg-1 text-fg transition-colors hover:border-accent hover:text-accent"
            aria-label={menuOpen ? '关闭导航菜单' : '打开导航菜单'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="text-lg leading-none">{menuOpen ? '×' : '☰'}</span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-border bg-bg/95 px-4 pb-4 pt-3 shadow-lg backdrop-blur-xl md:hidden">
          <nav className="grid gap-2" aria-label="移动端导航">
            {[...PRIMARY_NAV, ...SECONDARY_NAV].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center justify-between rounded-2xl border px-4 py-3 transition-colors',
                    isActive
                      ? 'border-border-accent bg-accent-soft text-accent'
                      : 'border-border bg-bg-1 text-fg-1 hover:border-accent hover:text-accent',
                  )
                }
              >
                <span className="font-medium">{item.label}</span>
                <span className="text-xs text-fg-muted">{item.desc}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-3 rounded-2xl border border-border bg-bg-1 p-3">
            {user ? (
              <div className="flex items-center justify-between gap-3">
                <Link to="/profile" onClick={() => setMenuOpen(false)} className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-fg">{user.name ?? '个人中心'}</p>
                  <p className="truncate text-xs text-fg-muted">{user.email}</p>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  登出
                </Button>
              </div>
            ) : (
              <Link to="/auth/login" onClick={() => setMenuOpen(false)}>
                <Button className="w-full rounded-xl">登录后查看命盘权益</Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
